/**
 * HTTP Service
 *
 * Why: Abstraction layes over Axios client
 * WHAT: Provides clean API for HTTP operations
 * BENEFIT:
 *   - Easy to swap HTTP client (axios -> fetch)
 *   - Automatic error transformation
 *   - Request cancellation support
 *   - Type-safe requests
 *
 * DESIGN DECISIONS:
 * 1. Singleton pattern for shared state (abort controllers)
 * 2. Automatic error handling with opt-out
 * 3. Request ID support for cancellation
 * 4. Unwraps response data automatically
 */

import { axiosClient } from "../client";
import type { IHttpService, RequestOptions } from "./types";
import { AxiosErrorHandler } from "@/core/errors/handlers";

import type { ErrorContext } from "@/core/errors/types";
import { logger } from "@/core/utils";
/**
 * HTTP Service Class
 *
 * Provides clean HTTP methods with error handling.
 */
class HttpService implements IHttpService {
  /**
   * Map of active abort controllers
   * key:requestId
   * value: AbortController
   */

  private abortControllers: Map<string, AbortController> = new Map();

  /**
   * Create abort signal for request
   * @param resqusetId
   * @returns AbortSignal | undefined
   */
  private getAbortSignal(requestId?: string): AbortSignal | undefined {
    if (!requestId) return undefined;

    // Cancel existing request with same ID
    this.cancelRequest(requestId);

    // Create new controller
    const controller = new AbortController();
    this.abortControllers.set(requestId, controller);
    return controller.signal;
  }

  /**
   * Cancel a pending request by ID
   */
  cancelRequest(requestId: string): void {
    const controller = this.abortControllers.get(requestId);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(requestId);
    }
  }

  /**
   * Cancel all requests
   */
  cancelAllRequests(): void {
    this.abortControllers.forEach((controller) => controller.abort());
    this.abortControllers.clear();
  }

  /**
   * Execute HTTP request with error handling
   */
  private async request<T>(
    method: string,
    url: string,
    options: RequestOptions = {},
    data?: unknown,
  ): Promise<T> {
    const { requestId, skipErrorHandling = false, ...axiosConfig } = options;

    // Get abort signal
    const signal = this.getAbortSignal(requestId);

    // Build error context
    const errorContext: Partial<ErrorContext> = {
      method,
      url,
      action: `HTTP ${method}`,
    };
    try {
      // Make request
      const response = await axiosClient.request<T>({
        method,
        url,
        data,
        signal,
        ...axiosConfig,
      });
      logger.info("Response of request in httpService", { response });
      // Cleanup abort controller
      if (requestId) {
        this.abortControllers.delete(requestId);
      }
      //returns unwrapped data
      return response.data;
    } catch (error) {
      // Cleanup abort controller
      if (requestId) {
        this.abortControllers.delete(requestId);
      }
      // Skip error handling if requested
      if (skipErrorHandling) {
        throw error;
      }

      // Transform and throw error
      throw AxiosErrorHandler.handle(error, { context: errorContext });
    }
  }

  /**
   * Unwraps Axios response to return data directly
   * Why: Callers don't need to access response.data
   */

  /**
   * Get request
   * @param url - Request URL
   * @param config - Request configuration
   * @return Response data
   */
  async get<T = unknown>(url: string, options?: RequestOptions): Promise<T> {
    // logger.debug(`HTTP GET: ${url}`);
    // const response = await axiosClient.get<T>(url, config);
    // return this.unwrapResponse(response);
    return this.request<T>("GET", url, options);
  }

  /**
   * POST Request
   * @param url - Request URL
   * @param data - Request Body
   * @param config - Request configuration
   * @returns Response data
   */
  async post<T = unknown>(
    url: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    // logger.debug(`HTTP POST: ${url}`, data);
    // const response = await axiosClient.post<T>(url, data, config);
    // return this.unwrapResponse(response);
    return this.request<T>("POST", url, options, data);
  }

  /**
   * Put request
   * @param url - Request url
   * @param data - Request body
   * @param config - Request configuration
   * @returns Response data
   */
  async put<T = unknown>(
    url: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    // logger.debug(`Http PUT: ${url}`, data);
    // const response = await axiosClient.put<T>(url, data, config);
    // return this.unwrapResponse(response);
    return this.request<T>("PUT", url, options, data);
  }

  /**
   * Patch request
   * @param url - Request url
   * @param data - Request body
   * @param config - Request configuration
   * @return response data
   */

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    // logger.debug(`HTTP PATCH: ${url}`, data);
    // const response = await axiosClient.patch<T>(url, data, config);
    // return this.unwrapResponse(response);
    return this.request<T>("PATCH", url, options, data);
  }

  /**
   * DELETE Request
   * @param url - Request URL
   * @param config - Request Configuration
   * @return Response data
   */
  async delete<T = unknown>(url: string, options?: RequestOptions): Promise<T> {
    // logger.debug(`HTTP DELETE: ${url}`);
    // const response = await axiosClient.delete<T>(url, config);
    // return this.unwrapResponse(response);
    return this.request<T>("DELETE", url, options);
  }

  /**
   * Upload file with progress tracking
   */
  async upload<T = unknown>(
    url: string,
    file: File | FormData,
    options?: RequestOptions & {
      onProgress?: (percent: number) => void;
    },
  ): Promise<T> {
    const { onProgress, ...restOptions } = options ?? {};

    // Prepare form data
    const formData =
      file instanceof FormData
        ? file
        : (() => {
            const fd = new FormData();
            fd.append("file", file);
            return fd;
          })();

    return this.post<T>(url, formData, {
      ...restOptions,
      headers: {
        "Content-Type": "multipart/form-data",
        ...restOptions?.headers,
      },
      onUploadProgress: onProgress
        ? (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              onProgress(percent);
            }
          }
        : undefined,
    });
  }

  /**
   * Download file
   */
  async download(
    url: string,
    filename: string,
    options?: RequestOptions,
  ): Promise<void> {
    const response = await axiosClient.get(url, {
      ...options,
      responseType: "blob",
    });

    // Create download link
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

// Export singleton instance
export const httpService = new HttpService();
